
import * as THREE from "../../libs/three.js/build/three.module.js";
import {Line2} from "../../libs/three.js/lines/Line2.js";
import {LineGeometry} from "../../libs/three.js/lines/LineGeometry.js";
import {LineMaterial} from "../../libs/three.js/lines/LineMaterial.js";
import {createStyledLabel} from "./DigitizationStyle.js";
import {setLabelWanted, setLabelContent} from "./labels/labelState.js";
import {computeScreenSpaceScale, ScreenSpaceSizes} from "./ScreenSpaceScale.js";
import {createCopyIconMarker, wireCopyIconText, positionCopyIconAtLabelCorner} from "./CopyIconMarker.js";
import {
	angleAtVertex, angleBisector, angleArcPoints,
	circumcircle, circleRingPoints,
	gridAzimuth, horizontalDistance, azimuthArcPoints,
} from "./geometry/measurementGeometry.js";

// Dekorative hjælpe-objekter til Mål vinkel / Mål cirkel / Mål azimut - samme
// kontrakt som HeightAuxiliaryVisual.js (Mål højde): IKKE selv en registreret
// feature, blot et barn af den rigtige FeatureVisual (`visual.add(aux)` +
// `visual.userData.auxiliaryVisual = aux`), så den flytter/skjules/slettes
// sammen med featuren, og den generiske hook-kæde i FeatureVisual.js
// (setLabelsVisible/dispose) og DigitizationModule.js (per-frame update)
// driver den uden værktøjsspecifik kode. Alle tre deler en lille base-klasse
// (linjer/labels/kopi-ikoner/skalering/oprydning), så kun selve geometrien er
// forskellig. Tekst-konventioner som de øvrige Målinger: længder med
// millimeter-præcision, vinkler med dansk "°".

const AUX_COLOR = 0xffdc00; // samme gule hjælpe-/konstruktionsfarve som resten af kodebasen

function formatLength(meters){ return `${meters.toFixed(3)} m`; }

function createAuxLine(dashed){
	const geometry = new LineGeometry();
	geometry.setPositions([0, 0, 0, 0, 0, 0]);
	const material = new LineMaterial({
		color: AUX_COLOR, linewidth: 2, dashed, dashSize: 0.3, gapSize: 0.18,
		resolution: new THREE.Vector2(1000, 1000),
	});
	material.depthTest = false;
	const line = new Line2(geometry, material);
	line.computeLineDistances();
	line.visible = false;
	return line;
}

// points: array af [x,y,z]. Linjens position sættes til første punkt og
// vertices gemmes LOKALT (relativt) - samme præcisionsgreb som FeatureVisuals
// _layoutEdges() (store UTM-koordinater i en Float32-buffer giver rystende linjer).
function setPolyline(line, points){
	const origin = points[0];
	line.position.set(origin[0], origin[1], origin[2]);
	const flat = [];
	for(const p of points) flat.push(p[0] - origin[0], p[1] - origin[1], p[2] - origin[2]);
	line.geometry.setPositions(flat);
	line.geometry.computeBoundingSphere();
	line.computeLineDistances();
}

const toArray = (v) => [v.x, v.y, v.z];

class MeasureAuxiliaryVisual extends THREE.Object3D {
	constructor(){
		super();
		this._lines = [];
		this._labels = [];
		this._copyIcons = [];
		this._markers = [];
		this.labelsVisible = true;
		this._hasData = false;
	}

	_addLine(dashed){
		const line = createAuxLine(dashed);
		this._lines.push(line);
		this.add(line);
		return line;
	}

	_addLabel(withCopyIcon){
		const label = createStyledLabel("");
		label.visible = false;
		this._labels.push(label);
		this.add(label);
		if(withCopyIcon){
			const icon = createCopyIconMarker();
			wireCopyIconText(icon, () => label.text || null);
			this._copyIcons.push({icon, label});
			this.add(icon);
		}
		return label;
	}

	_addMarker(){
		const material = new THREE.MeshLambertMaterial({color: AUX_COLOR, depthTest: false, depthWrite: false});
		const marker = new THREE.Mesh(new THREE.SphereGeometry(1, 16, 16), material); // kugle (var oktaeder/"pyramide")
		marker.visible = false;
		this._markers.push(marker);
		this.add(marker);
		return marker;
	}

	setLabelsVisible(visible){
		this.labelsVisible = visible;
		this._refreshLabelVisibility();
	}

	_setHasData(has){
		this._hasData = has;
		for(const line of this._lines) line.visible = has && line.userData.drawable !== false;
		for(const marker of this._markers) marker.visible = has;
		this._refreshLabelVisibility();
	}

	_refreshLabelVisibility(){
		for(const label of this._labels) setLabelWanted(label, this._hasData && this.labelsVisible);
	}
	// Labels, som LabelManager (labels/) styrer: hjælpelabels udfases tidligt og vises kun ved valg/nærzoom (LABEL-STRATEGI.md 12.4)
	auxLabels(){
		return this._labels;
	}

	update(camera, clientWidth, clientHeight){
		for(const line of this._lines) line.material.resolution.set(clientWidth, clientHeight);

		for(const label of this._labels){
			if(!label.visible) continue;
			const scale = computeScreenSpaceScale(
				camera, label.getWorldPosition(new THREE.Vector3()), camera.position,
				clientWidth, clientHeight, ScreenSpaceSizes.label);
			label.scale.set(scale, scale, scale);
		}
		for(const marker of this._markers){
			if(!marker.visible) continue;
			const scale = computeScreenSpaceScale(
				camera, marker.getWorldPosition(new THREE.Vector3()), camera.position,
				clientWidth, clientHeight, ScreenSpaceSizes.computedPoint);
			marker.scale.set(scale, scale, scale);
		}
		for(const {icon, label} of this._copyIcons){
			positionCopyIconAtLabelCorner(icon, label, camera, clientWidth, clientHeight);
		}
	}

	dispose(){
		for(const line of this._lines){ line.geometry.dispose(); line.material.dispose(); }
		for(const label of this._labels){ label.dispose(); label.userData.pvRec = null; }
		for(const marker of this._markers){ marker.geometry.dispose(); marker.material.dispose(); }
		for(const {icon} of this._copyIcons){
			icon.geometry.dispose();
			icon.material.map.dispose();
			icon.material.dispose();
		}
	}
}

// Mål vinkel: bue i midterpunktet + en vinkel-label. a/vertex/b er de 3
// rigtige, redigerbare punkter (THREE.Vector3, world-positioner).
export class AngleAuxiliaryVisual extends MeasureAuxiliaryVisual {
	constructor(){
		super();
		this.arc = this._addLine(false);
		this.angleLabel = this._addLabel(true);
	}

	updateFromPoints(a, vertex, b){
		if(!a || !vertex || !b){ this._setHasData(false); return; }

		const pa = toArray(a), pv = toArray(vertex), pb = toArray(b);
		const angle = angleAtVertex(pv, pa, pb);
		if(angle === null){ this._setHasData(false); return; }

		const legMin = Math.min(a.distanceTo(vertex), b.distanceTo(vertex));
		const radius = legMin * 0.25;
		const arcPoints = angleArcPoints(pv, pa, pb, radius);
		this.arc.userData.drawable = !!arcPoints; // 0/180 grader: ingen bue (ingen plan), men label står stadig
		if(arcPoints) setPolyline(this.arc, arcPoints);

		const bisector = angleBisector(pv, pa, pb);
		const labelDistance = radius * 1.7;
		const labelPos = bisector
			? [pv[0] + bisector[0] * labelDistance, pv[1] + bisector[1] * labelDistance, pv[2] + bisector[2] * labelDistance]
			: pv;
		this.angleLabel.position.set(labelPos[0], labelPos[1], labelPos[2]);
		setLabelContent(this.angleLabel, `${angle.toFixed(1)}°`);

		this._setHasData(true);
	}
}

// Mål cirkel: cirklen gennem de 3 punkter (i planen gennem dem), en stiplet
// radiuslinje fra centrum, et centrum-mærke og en radius-label. De 3 punkter
// er cirklens periferi - samme definition som det gamle native Cirkel-måleværktøj.
export class CircleAuxiliaryVisual extends MeasureAuxiliaryVisual {
	constructor(){
		super();
		this.ring = this._addLine(false);
		this.radiusLine = this._addLine(true);
		this.centerMarker = this._addMarker();
		this.radiusLabel = this._addLabel(false); // længde-label: ingen kopi-ikon, som segment-labels
		this._circle = null;
	}

	get circle(){ return this._circle; }

	updateFromPoints(a, b, c){
		const circle = (a && b && c) ? circumcircle(toArray(a), toArray(b), toArray(c)) : null;
		this._circle = circle;
		if(!circle){ this._setHasData(false); return; }

		setPolyline(this.ring, circleRingPoints(circle, 64));
		setPolyline(this.radiusLine, [circle.center, toArray(b)]);
		this.centerMarker.position.set(circle.center[0], circle.center[1], circle.center[2]);

		const mid = [(circle.center[0] + b.x) / 2, (circle.center[1] + b.y) / 2, (circle.center[2] + b.z) / 2];
		this.radiusLabel.position.set(mid[0], mid[1], mid[2]);
		setLabelContent(this.radiusLabel, `Radius: ${formatLength(circle.radius)}`);

		this._setHasData(true);
	}
}

// Mål azimut: nordlinje (nettets nord = +Y) og vandret projektion af målet fra
// punkt 1, med en vandret bue imellem og en azimut-label. Alt tegnes i punkt
// 1's kote - azimut er en vandret retning (se gridAzimuth()).
export class AzimuthAuxiliaryVisual extends MeasureAuxiliaryVisual {
	constructor(){
		super();
		this.northLine = this._addLine(true);
		this.targetLine = this._addLine(true);
		this.arc = this._addLine(false);
		this.northLabel = this._addLabel(false);
		this.azimuthLabel = this._addLabel(true);
	}

	updateFromPoints(from, to){
		if(!from || !to){ this._setHasData(false); return; }

		const p0 = toArray(from), p1 = toArray(to);
		const azimuth = gridAzimuth(p0, p1);
		if(azimuth === null){ this._setHasData(false); return; }

		const d = horizontalDistance(p0, p1);
		const north = [p0[0], p0[1] + d, p0[2]];
		const target = [p1[0], p1[1], p0[2]];
		setPolyline(this.northLine, [p0, north]);
		setPolyline(this.targetLine, [p0, target]);
		setPolyline(this.arc, azimuthArcPoints(p0, azimuth, d * 0.6));

		setLabelContent(this.northLabel, "N");
		this.northLabel.position.set(north[0], north[1] + d * 0.04, north[2]);

		const midAngle = (azimuth / 2) * Math.PI / 180;
		const labelRadius = d * 0.72;
		setLabelContent(this.azimuthLabel, `${azimuth.toFixed(2)}°`);
		this.azimuthLabel.position.set(p0[0] + labelRadius * Math.sin(midAngle), p0[1] + labelRadius * Math.cos(midAngle), p0[2]);

		this._setHasData(true);
	}
}
